import { useQuery } from '@tanstack/react-query'
import { AdvancedMarker } from '@vis.gl/react-google-maps'
import type { SyntheticEvent } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { searchParcels } from '~/api'
import { GoogleMapContainedPopover } from '../google/GoogleMapContainedPopover'
import type { ParcelProperties } from '~/models'
import MapParcelPopup from './ParcelPopup'

type GoogleParcelPopupProps = {
  latitude: number
  longitude: number
  onClose: () => void
  parcelId?: string | null
  pixel?: { x: number; y: number }
}

function stopMapEvent(event: SyntheticEvent) {
  event.stopPropagation()
}

export function GoogleParcelPopup({
  latitude,
  longitude,
  onClose,
  parcelId,
  pixel,
}: GoogleParcelPopupProps) {
  const [selectedParcelIndex, setSelectedParcelIndex] = useState<number | null>(
    null
  )
  const parcelQuery = useQuery({
    queryKey: ['google-parcel-popup', latitude, longitude, parcelId],
    queryFn: () => searchParcels(latitude, longitude, 100),
  })

  const parcels: ParcelProperties[] = useMemo(() => {
    const features = parcelQuery.data?.features ?? []

    return features.map((feature) => feature.properties)
  }, [parcelQuery.data])

  useEffect(() => {
    if (parcels.length === 0) {
      setSelectedParcelIndex(null)
      return
    }

    const selectedIndex = parcelId
      ? parcels.findIndex((parcel) => parcel.ll_uuid === parcelId)
      : -1

    setSelectedParcelIndex(selectedIndex >= 0 ? selectedIndex : 0)
  }, [parcelId, parcels])

  const selectedParcelData =
    selectedParcelIndex === null ? null : parcels[selectedParcelIndex] ?? null

  const content = (
    <div
      className="relative z-20 w-[400px] max-w-[calc(100vw-3rem)] rounded-md bg-background shadow-lg"
      onClick={stopMapEvent}
      onDoubleClick={stopMapEvent}
      onMouseDown={stopMapEvent}
      onPointerDown={stopMapEvent}
      onTouchMove={stopMapEvent}
      onWheel={stopMapEvent}>
      <button
        aria-label="Close parcel popup"
        className="absolute right-2 top-2 z-10 rounded-md bg-background px-2 py-1 text-lg leading-none shadow-sm"
        onClick={onClose}
        type="button">
        x
      </button>
      <MapParcelPopup
        parcels={parcels}
        selectedParcelData={selectedParcelData}
        selectedParcelIndex={selectedParcelIndex}
        setSelectedParcelIndex={setSelectedParcelIndex}
      />
    </div>
  )

  if (pixel) {
    return (
      <GoogleMapContainedPopover
        className="absolute z-[60]"
        defaultSize={{ height: 440, width: 400 }}
        pixel={pixel}>
        {content}
      </GoogleMapContainedPopover>
    )
  }

  return (
    <AdvancedMarker
      position={{ lat: latitude, lng: longitude }}
      anchorLeft="-50%"
      anchorTop="calc(-100% - 10px)"
      zIndex={60}>
      {content}
      <div className="absolute left-1/2 top-full z-10 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-background" />
    </AdvancedMarker>
  )
}
